import type { Package, PackageVersion, PackageListResponse, CreatePackageRequest, PublishVersionRequest, PublishVersionResponse } from '../types/packages'
import { fetchAPI } from './core'

export interface PackageListParams {
  q?: string
  language?: string
  tag?: string
  official?: boolean
  page?: number
  limit?: number
  sort?: string
}

export async function listPackages(params?: PackageListParams): Promise<PackageListResponse> {
  const qs = new URLSearchParams()
  if (params?.q) qs.set('q', params.q)
  if (params?.language) qs.set('language', params.language)
  if (params?.tag) qs.set('tag', params.tag)
  if (params?.official !== undefined) qs.set('official', String(params.official))
  if (params?.page) qs.set('page', String(params.page))
  if (params?.limit) qs.set('limit', String(params.limit))
  if (params?.sort) qs.set('sort', params.sort)
  const query = qs.toString()
  return fetchAPI(`/packages${query ? `?${query}` : ''}`, { skipAuth: true })
}

export async function getFeaturedPackages(): Promise<Package[]> {
  return fetchAPI('/packages/featured', { skipAuth: true })
}

export async function getPackage(slug: string): Promise<Package> {
  return fetchAPI(`/packages/${slug}`, { skipAuth: true })
}

export async function listPackageVersions(slug: string): Promise<PackageVersion[]> {
  return fetchAPI(`/packages/${slug}/versions`, { skipAuth: true })
}

export async function getPackageVersion(slug: string, version: string): Promise<PackageVersion> {
  return fetchAPI(`/packages/${slug}/versions/${version}`, { skipAuth: true })
}

export async function createPackage(data: CreatePackageRequest): Promise<Package> {
  return fetchAPI('/packages', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function updatePackage(slug: string, data: Partial<CreatePackageRequest>): Promise<Package> {
  return fetchAPI(`/packages/${slug}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

export async function deletePackage(slug: string): Promise<{ message: string }> {
  return fetchAPI(`/packages/${slug}`, { method: 'DELETE' })
}

export async function publishPackageVersion(slug: string, data: PublishVersionRequest): Promise<PublishVersionResponse> {
  return fetchAPI(`/packages/${slug}/versions`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function listMyPackages(): Promise<Package[]> {
  const data = await fetchAPI<{ packages: Package[]; total: number }>('/user/packages')
  return data.packages ?? []
}

export async function markPackageOfficial(slug: string, official: boolean): Promise<{ message: string; is_official: boolean }> {
  return fetchAPI(`/admin/packages/${slug}/official`, {
    method: 'PUT',
    body: JSON.stringify({ official }),
  })
}

export async function uploadPackageIcon(slug: string, file: File): Promise<{ icon_url: string }> {
  const formData = new FormData()
  formData.append('icon', file)
  return fetchAPI(`/packages/${slug}/icon`, {
    method: 'POST',
    body: formData,
  })
}

export async function deletePackageIcon(slug: string): Promise<{ message: string }> {
  return fetchAPI(`/packages/${slug}/icon`, { method: 'DELETE' })
}

export async function getDownloadSparkline(slug: string): Promise<Array<{ date: string; count: number }>> {
  const data = await fetchAPI<{ sparkline: Array<{ date: string; count: number }> }>(`/packages/${slug}/downloads/sparkline`)
  return data.sparkline ?? []
}
